import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu
} from '@material-ui/core';
import { Delete, MoreVert, NavigateNext } from '@material-ui/icons';
import {
  InfiniteScrollGrid,
  useBaseStore
} from '@roc/feature-app-core';
import { DraftLoan } from '@roc/feature-types';
import {
  ConfirmationMenuItem, LinkColumn, MenuItem, SelectFilterComponent,
  SelectFloatingFilterComponent, StatusChip,
  StatusType, TextColumn
} from '@roc/ui';
import { observer } from 'mobx-react';
import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { routeByLoanSubType, useLoanSubmissionRoutes } from '../../loanSubmission';
import { useLoanRoutes } from '@roc/feature-loans-routes-config';
import { useLoanStore } from '../hooks/useLoanStore';
import { useOneToolRoutes } from 'libs/feature-one-tool/src/quote/hooks/useOneToolRoutes';
import { capitalProviderOptions, LoanType, CapitalProvider } from '@roc/feature-utils';
import { editLoanRouteByLoanSubType, routeByLoanSubType as thirdPartyRouteByLoanSubType, getThirdPartyLoanRoutesConfig, useThirdPartyLoanRoutes } from '@roc/feature-third-party-loans';

const addCapitalProviderColumn = columns => {
  const capitalProviderColumn = {
    field: 'capitalProvider',
    headerName: 'Capital Provider',
    minWidth: 150,
    sortable: false,
    filter: 'selectFilter',
    floatingFilter: true,
    floatingFilterComponent: 'selectFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
      options: capitalProviderOptions,
    },
  };
  return [capitalProviderColumn, ...columns];
};

const isExternalCapital = row => {
  const externalCapitalLabel = capitalProviderOptions.find(
    opt => opt.value === CapitalProvider.EXTERNAL_CAPITAL
  ).label;
  return row?.capitalProvider === externalCapitalLabel;
};


export const MyQuoteLoansGrid = observer(({ columns }) => {
  const { globalStore } = useBaseStore();
  const { loanStore } = useLoanStore();
  const { loanRoutesConfig } = useLoanRoutes();
  const { loanSubmissionRoutesConfig } = useLoanSubmissionRoutes();
  const { thirdPartyLoanRoutesConfig } = useThirdPartyLoanRoutes();
  const { oneToolRoutesConfig } = useOneToolRoutes();
  const [loanId, setLoanId] = useState(0);
  const [currentRow, setCurrentRow] = useState(null);
  const [anchorEl, setAnchorEl] = useState<HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);
  const { push } = useHistory();
  
  const gridStore = globalStore.showUnifiedLoans
    ? loanStore.unifiedMyLoanQuotesGridStore
    : loanStore.myLoanQuotesGridStore;

  const _columns = useMemo(() => {
    if (globalStore.showUnifiedLoans) {
      return addCapitalProviderColumn(columns);
    } else {
      return columns;
    }
  }, [columns]);

  const navigateToContinue = (row) => {
    if (isExternalCapital(row)) {
      const route = thirdPartyLoanRoutesConfig[thirdPartyRouteByLoanSubType[row?.loanSubtype]];
      push(route(row?.quoteNumber).url);
    } else if (row?.draftLoanId) {
      const continueDraftLoanRequest = {
        loanType: LoanType.RESIDENTIAL_TERM,
        loanSubType: row?.loanSubtype,
        id: row.draftLoanId,
      };
      loanStore.continueDraftLoan(continueDraftLoanRequest as DraftLoan);
      push(loanSubmissionRoutesConfig.submitloan.children[routeByLoanSubType[row?.loanSubtype]].url);
    } else {
      const route = oneToolRoutesConfig[routeByLoanSubType[row?.loanSubtype]];
      push(route(row?.loanQuoteId).url);
    }
  };

  const frameworkComponents = {
    selectFilter: SelectFilterComponent,
    selectFloatingFilter: SelectFloatingFilterComponent,
    propertyAddressCellRenderer: params => {
      return LinkColumn({
        ...params,
        onClick: () => navigateToContinue(params.node.data),
      });
    },
    loanIdCellRenderer: params => {
      const data = params?.node?.data;
      const externalCapitalRoute =
        thirdPartyLoanRoutesConfig[editLoanRouteByLoanSubType[data?.loanType]];
              
      return loanStore.isLoanSubtypeSupported(params.node.data?.loanSubType)
        ? LinkColumn({
          ...params,
          url: isExternalCapital(data) ? externalCapitalRoute(data?.quoteNumber).url : loanRoutesConfig.loans(params.value).children.dashboard.url,
        })
        : TextColumn(params);
    },
    statusCellRenderer: ({ value }) =>
      value ? (
        <StatusChip label={value} size="small" statusType={StatusType.LOAN} />
      ) : null,
    actionCellRenderer: params => {
      return (
        <IconButton onClick={e => openMenu(e, params.node.data)}>
          <MoreVert color="primary" />
        </IconButton>
      );
    },
  };

  const openMenu = (event: React.MouseEvent<HTMLElement>, record) => {
    const { id } = record;
    setLoanId(id);
    setCurrentRow(record);
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <InfiniteScrollGrid
        columns={_columns}
        frameworkComponents={frameworkComponents}
        store={gridStore}
      />
      <Menu
        id="menu-appbar"
        getContentAnchorEl={null}
        anchorEl={anchorEl}
        keepMounted
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={menuOpen}
        onClose={handleMenuClose}
      >
        <MenuItem
          testId={`continue-loan-${loanId}`}
          onClick={() => {
            handleMenuClose();
            navigateToContinue(currentRow);
          }}
        >
          <ListItemIcon>
            <NavigateNext fontSize="small" />
          </ListItemIcon>
          <ListItemText>Continue</ListItemText>
        </MenuItem>
        <ConfirmationMenuItem
          testId={`delete-loan-${loanId}`}
          onClick={() => {
            if (isExternalCapital(currentRow)) {
              loanStore.deleteExternalQuote(currentRow?.quoteNumber);
            } else if (currentRow?.draftLoanId) {
              loanStore.deleteDraftLoan(currentRow?.draftLoanId, true);
            } else {
              loanStore.deleteQuote(currentRow?.loanQuoteId);
            }
            handleMenuClose();
          }}
          confirmDialogProps={{
            body: `Are you sure you want to delete this ${currentRow?.draftLoanId ? 'draft loan' : 'quote'}?`,
          }}
        >
          <ListItemIcon>
            <Delete fontSize="small" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </ConfirmationMenuItem>
      </Menu>
    </>
  );
});
